import { useEffect, useState } from "react"
import { skipToken } from "@reduxjs/toolkit/query"
import { useGetPublicationsQuery } from "../../services/publications/publicationsApi"
import { useChannelsQuery } from "../../services/channels/channelsApi"
import { ResourceSelect } from "../common/resourceSelect"
import { isChannelId } from "../../utils/placementDraft"
import type { Publication } from "../../interface/publications"

interface Option {
  id: string
  name: string
}
interface Props {
  value: string
  onChange: (value: string) => void
  disabled: boolean
}
export function PlacementPublicationSelect({
  value,
  onChange,
  disabled,
  selected,
}: Props & { selected?: Publication }) {
  const [open, setOpen] = useState(false)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState("")
  const [options, setOptions] = useState<Option[]>([])
  const query = useGetPublicationsQuery(open ? { page, limit: 50 } : skipToken)
  const current = !query.error ? query.currentData : undefined
  useEffect(() => {
    if (!current || current.page !== page) return
    setOptions((previous) => [
      ...new Map(
        [
          ...previous,
          ...current.data.map(({ id, name }) => ({ id, name })),
        ].map((item) => [item.id, item]),
      ).values(),
    ])
  }, [current, page])
  return (
    <ResourceSelect
      label="Публикация"
      size="small"
      value={
        (selected?.id === value ? selected : undefined) ??
        options.find((item) => item.id === value) ??
        (value ? { id: value, name: "Выбранная публикация" } : null)
      }
      options={options.filter((item) =>
        `${item.name} ${item.id}`.toLowerCase().includes(search.toLowerCase()),
      )}
      getOptionKey={(item) => item.id}
      getOptionLabel={(item) => item.name}
      onChange={(item) => onChange(item?.id ?? "")}
      onSearch={setSearch}
      onOpen={() => setOpen(true)}
      onLoadMore={() => {
        if (current && page < current.totalPages && !query.isFetching)
          setPage(page + 1)
      }}
      hasMore={!!current && page < current.totalPages}
      isFetching={query.isFetching}
      disabled={disabled}
      error={query.error}
      onRetry={() => void query.refetch()}
    />
  )
}

/** Retained /channel is an unpaginated array with only withHidden; do not invent
 * page/search params. Filtering this already returned catalog is local. */
export function PlacementChannelSelect({ value, onChange, disabled }: Props) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const [retained, setRetained] = useState<Option>()
  const query = useChannelsQuery(
    open ? { queryParams: { withHidden: true } } : skipToken,
  )
  const options = (!query.error ? (query.currentData ?? []) : [])
    .filter((channel) => isChannelId(channel.id))
    .map((channel) => ({
      id: channel.id,
      name: `${channel.custom_name || channel.title} (${channel.id})`,
    }))
  const selected =
    retained?.id === value
      ? retained
      : (options.find((item) => item.id === value) ??
        (value ? { id: value, name: value } : null))
  return (
    <ResourceSelect
      label="Канал"
      size="small"
      value={selected}
      options={options.filter((item) =>
        item.name.toLowerCase().includes(search.toLowerCase()),
      )}
      getOptionKey={(item) => item.id}
      getOptionLabel={(item) => item.name}
      onChange={(item) => {
        setRetained(item ?? undefined)
        onChange(item?.id ?? "")
      }}
      onSearch={setSearch}
      onOpen={() => setOpen(true)}
      onLoadMore={() => {}}
      hasMore={false}
      isFetching={query.isFetching}
      disabled={disabled}
      error={query.error}
      onRetry={() => void query.refetch()}
    />
  )
}
